import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AreasState, CustomAction } from "./types";
import { resetLoginFeedbackMessage } from "./authSlice";

type initialStateType = {
  areasState: AreasState;
};
const areasState: AreasState = {
  loading: false,
  error: null,
  areas: [],
  counties: [],
  regions: [],
  outages: [],
  outageList: { items: [], total: 0, page: 1, pageSize: 10 },
  outageBlogs: [],
  feedbackMessage: {
    message: null,
    isError: false,
    open: false
  }
};

const initialState: initialStateType = {
  areasState,
};

export const areasSlice = createSlice({
  name: "areas",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.areasState.loading = action.payload;
    },
    showAlert: (state, action) => {
      state.areasState.error = action.payload;
    },
    fetchRegions: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        state.areasState.loading = true;
      } else if (action?.payload?.methodRequestState === 'finished') {
        if (action.payload.result) {
          state.areasState.regions = action.payload.result;
        } else {
          state.areasState.error = action.payload.error;
        }
        state.areasState.loading = false;
      }
    },
    fetchCounties: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        state.areasState.loading = true;
      } else if (action?.payload?.methodRequestState === 'finished') {
        if (action.payload.result) {
          state.areasState.counties = action.payload.result;
        } else {
          state.areasState.error = action.payload.error;
        }
        state.areasState.loading = false;
      }
    },
    fetchAreas: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        state.areasState.loading = true;
      } else if (action?.payload?.methodRequestState === 'finished') {
        if (action.payload.result) {
          state.areasState.areas = action.payload.result;
        } else {
          state.areasState.error = action.payload.error;
        }
        state.areasState.loading = false;
      }
    },
    fetchOutages: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        state.areasState.loading = true;
      } else if (action?.payload?.methodRequestState === 'finished') {
        if (action.payload.result) {
          const outages = action.payload.result;
          state.areasState.outages = outages
        } else if (action.payload.error) {
          state.areasState.feedbackMessage = {
            message: action.payload.error?.message || 'Something went wrong',
            isError: true,
            open: true
        };
          state.areasState.outages = initialState.areasState.outages;
        }
        state.areasState.loading = false;
      }
    },
    searchOutages: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        state.areasState.loading = true;
      } else if (action?.payload?.methodRequestState === 'finished') {
        if (action.payload.result) {
          const outages = action.payload.result;
          state.areasState.outages = outages
        } else if (action.payload.error) {
          state.areasState.feedbackMessage = {
            message: action.payload.error?.message || 'No outages found for this area',
            isError: true,
            open: true
          };
          state.areasState.outages = initialState.areasState.outages;
        }
        state.areasState.loading = false;
      }
    },
    fetchOutageList: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        state.areasState.loading = true;
      } else if (action?.payload?.methodRequestState === 'finished') {
        if (action.payload.result) {
          state.areasState.outageList = action.payload.result;
        } else if (action.payload.error) {
          state.areasState.feedbackMessage = {
            message: action.payload.error?.message || 'Something went wrong',
            isError: true,
            open: true
          };
          state.areasState.outageList = initialState.areasState.outageList;
        }
        state.areasState.loading = false;
      }
    },
    resetDropdownData: (state, action) => {
      const {params} = action.payload;
      if (params.includes('counties')) {
        state.areasState.counties = [];
      }
      if (params.includes('areas')) {
        state.areasState.areas = [];
      }
    },
    resetFeedbackMessage: (state) => {
      state.areasState.feedbackMessage = {
        message: null,
        isError: false,
        open: false,
      }
    },
    fetchOutageBlogs: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        state.areasState.loading = true;
      } else if (action?.payload?.methodRequestState === 'finished') {
        if (action.payload.result) {
          const blogs = action.payload.result;
          
          // Find the latest blog by highest obg_id
          const sortedBlogs = [...blogs].sort((a, b) => b.obg_id - a.obg_id);
          const latestBlog = sortedBlogs[0];
          
          // Clone the latest blog and modify it for "latest" access
          const latestBlogClone = {
            ...latestBlog,
            urlPath: "latest"
          };
          
          // Prepend the cloned blog to the result array
          const modifiedBlogs = [latestBlogClone, ...blogs];
          
          state.areasState.outageBlogs = modifiedBlogs;
        } else if (action.payload.error) {
          state.areasState.feedbackMessage = {
            message: action.payload.error?.message || 'Failed to load outage blog data',
            isError: true,
            open: true
          };
        }
        state.areasState.loading = false;
      }
    },
    recordBlogView: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        // No loading state needed for view tracking
      } else if (action?.payload?.methodRequestState === 'finished') {
        // Optionally update the view count in the local state if API returns updated blog
        if (action.payload.result) {
          const updatedBlog = action.payload.result;
          // Update the specific blog in outageBlogs array
          if (Array.isArray(state.areasState.outageBlogs)) {
            const blogIndex = state.areasState.outageBlogs.findIndex(
              (blog) => blog.obg_id === updatedBlog.obg_id
            );
            if (blogIndex !== -1) {
              state.areasState.outageBlogs[blogIndex] = updatedBlog;
            }
          } else if (state.areasState.outageBlogs?.obg_id === updatedBlog.obg_id) {
            // If it's a single blog object
            state.areasState.outageBlogs = updatedBlog;
          }
        }
      }
    },
    likeBlog: (state, action: PayloadAction<CustomAction>) => {
      if (action?.payload?.methodRequestState === 'requested') {
        // No loading state needed for like action
      } else if (action?.payload?.methodRequestState === 'finished') {
        if (action.payload.result) {
          const updatedBlog = action.payload.result;
          // Update the specific blog in outageBlogs array
          if (Array.isArray(state.areasState.outageBlogs)) {
            const blogIndex = state.areasState.outageBlogs.findIndex(
              (blog) => blog.obg_id === updatedBlog.obg_id
            );
            if (blogIndex !== -1) {
              state.areasState.outageBlogs[blogIndex] = updatedBlog;
            }
          } else if (state.areasState.outageBlogs?.obg_id === updatedBlog.obg_id) {
            // If it's a single blog object
            state.areasState.outageBlogs = updatedBlog;
          }
        } else if (action.payload.error) {
          state.areasState.feedbackMessage = {
            message: action.payload.error?.message || 'Failed to like blog',
            isError: true,
            open: true
          };
        }
      }
    },
  },
});

export const { 
  setLoading, 
  showAlert, 
  fetchRegions, 
  fetchCounties, 
  fetchAreas, 
  resetDropdownData, 
  fetchOutages, 
  searchOutages,
  fetchOutageList,
  resetFeedbackMessage,
  fetchOutageBlogs,
  recordBlogView,
  likeBlog
} = areasSlice.actions;

export const fetchRegionsAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: fetchRegions.type,
  payload: {
    params,
    method,
  }
});

export const fetchCountiesAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: fetchCounties.type,
  payload: {
    params,
    method,
  }
});

export const fetchAreasAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: fetchAreas.type,
  payload: {
    params,
    method,
  }
});

export const fetchOutagesAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: fetchOutages.type,
  payload: {
    params,
    method,
  }
});

export const searchOutagesAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: searchOutages.type,
  payload: {
    params,
    method,
  }
});

export const fetchOutageListAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: fetchOutageList.type,
  payload: {
    params,
    method,
  }
});

export const fetchOutageBlogsAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: fetchOutageBlogs.type,
  payload: {
    params,
    method,
  }
});

export const recordBlogViewAction = ({ params, method }: { params: { blogId: number | string }; method: { url: string; action: string } }) => ({
  type: recordBlogView.type,
  payload: {
    params,
    method,
  }
});

export const likeBlogAction = ({ params, method }: { params: { blogId: number | string }; method: { url: string; action: string } }) => ({
  type: likeBlog.type,
  payload: {
    params,
    method,
  }
});

export const selectAreasState = (state: RootState) => state.areas.areasState;
